import _get from 'lodash/get';
import _isNaN from 'lodash/isNaN';
import * as Yup from 'yup';
import { TFunction } from 'i18next';
import {
  FieldChangeCallBackArgs,
  FieldItem,
  Values,
} from '@lib/interfaces/form';
import Input from '@lib/components/Input/Input';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import CheckBoxAsToggle from '@lib/components/CheckBoxAsToggle/CheckBoxAsToggle';
import DatePicker from '@lib/components/DateTimePicker/DatePicker';
import RadioButtonGroup, {
  RadioButtonWrapViews,
} from '@lib/components/RadioButtonGroup/RadioButtonGroup';
import RadioButtonAsButton from '@lib/components/RadioButtonAsButton/RadioButtonAsButton';
import FrequencyFormFields from 'components/FrequencyFormFields/FrequencyFormFields';
import Typography from '@lib/components/Typography/Typography';
import formStyles from '@lib/assets/styles/Form.module.scss';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import {
  DocumentationRenewalRepetitionEnum,
  EdmDocumentRenewalReminderTemplateFragmentFragment,
} from 'graphql-common';
import { ButtonSizes } from '@lib/components/Button/Button';
import { DEFAULT_NUMBER_INPUT_PROPS } from '@lib/enums/form';
import getRenewalReminderTemplateValues from './getRenewalReminderTemplateValues';
import getRenewalRepetitionOptions from './getRenewalRepetitionOptions';
import styles from './DocumentFormFields.module.scss';

const RENEWAL_CUSTOM_DAYS_MAX = 3650;
const RENEWAL_EARLY_REMINDER_DAYS_MAX = 365;

export default function getRenewalReminderFields({
  t,
  template,
  hasPriorReminderValues,
  isPlanLocked,
}: {
  t: TFunction<'translation', undefined>;
  template?: EdmDocumentRenewalReminderTemplateFragmentFragment | null;
  hasPriorReminderValues?: boolean;
  isPlanLocked?: boolean;
}): FieldItem[] {
  return [
    {
      name: DocumentFormFieldNames.RenewalReminder,
      element: FrequencyFormFields,
      componentProps: {
        title: t('renewal-reminder'),
        titleTooltipText: t('renewal-reminder-tooltip'),
        name: DocumentFormFieldNames.RenewalReminder,
        disabled: isPlanLocked,
        isLocked: isPlanLocked,
        lockTooltipText: isPlanLocked
          ? t('unlock-renewal-reminder-tooltip')
          : undefined,
      },
      groupFields: [
        {
          name: DocumentFormFieldNames.RenewalReminderEnabled,
          element: CheckBoxSingleBool,
          componentProps: {
            CheckBoxComponent: CheckBoxAsToggle,
            options: [{ label: 'checked', value: 'checked' }],
            disabled: isPlanLocked,
          },
          isVisibleFn: () => false,
          fieldChangeCallBack: ({
            type,
            setValue,
            name,
            values,
          }: FieldChangeCallBackArgs) => {
            if (
              type !== 'change' ||
              name !== DocumentFormFieldNames.RenewalReminderEnabled
            ) {
              return;
            }
            const isNowEnabled = !!_get(
              values,
              DocumentFormFieldNames.RenewalReminderEnabled,
            );
            if (!isNowEnabled) return;
            if (hasPriorReminderValues) return;

            const fromTemplate = getRenewalReminderTemplateValues(t, template);
            if (fromTemplate) {
              Object.entries(fromTemplate).forEach(([key, value]) => {
                if (value !== undefined) {
                  setValue(key, value, { shouldValidate: true });
                }
              });
              return;
            }

            setValue(DocumentFormFieldNames.RenewalReminderEarlyAmount, 1, {
              shouldValidate: true,
            });
            setValue(
              DocumentFormFieldNames.RenewalReminderRepetition,
              getRenewalRepetitionOptions(t)[0],
              { shouldValidate: true },
            );
          },
        },
        {
          name: DocumentFormFieldNames.RenewalReminderDeadlineDate,
          label: t('deadline-date'),
          element: DatePicker,
          isVisibleFn: (values: Values) =>
            !!_get(values, DocumentFormFieldNames.RenewalReminderEnabled),
          validation: Yup.string().when(
            DocumentFormFieldNames.RenewalReminderEnabled,
            (deps, schema) =>
              _get(deps, 0)
                ? schema.required(t('required-field-error'))
                : schema.nullable(),
          ),
          isOptionalFn: () => false,
        },
        {
          name: DocumentFormFieldNames.RenewalReminderRepetitionTitle,
          isVisibleFn: (values: Values) =>
            !!_get(values, DocumentFormFieldNames.RenewalReminderEnabled),
          customRender: () => (
            <Typography variant="body" strong key="repetitionTitle">
              {t('repetition')}
            </Typography>
          ),
        },
        {
          name: DocumentFormFieldNames.RenewalReminderRepetition,
          element: RadioButtonGroup,
          isVisibleFn: (values: Values) =>
            !!_get(values, DocumentFormFieldNames.RenewalReminderEnabled),
          validation: Yup.object()
            .nullable()
            .when(
              DocumentFormFieldNames.RenewalReminderEnabled,
              (deps, schema) =>
                _get(deps, 0)
                  ? schema.required(t('required-field-error'))
                  : schema.nullable(),
            ),
          componentProps: {
            RadioButtonComponent: RadioButtonAsButton,
            radioButtonComponentProps: {
              buttonSize: ButtonSizes.small,
            },
            wrapView: RadioButtonWrapViews.row,
            options: getRenewalRepetitionOptions(t),
          },
          fieldChangeCallBack: ({
            type,
            setValue,
            name,
            values,
          }: FieldChangeCallBackArgs) => {
            if (
              type === 'change' &&
              name === DocumentFormFieldNames.RenewalReminderRepetition
            ) {
              const repetitionValue = _get(values, [
                DocumentFormFieldNames.RenewalReminderRepetition,
                'value',
              ]);
              if (
                repetitionValue === DocumentationRenewalRepetitionEnum.Custom
              ) {
                const currentCustomDays = _get(
                  values,
                  DocumentFormFieldNames.RenewalReminderCustomDays,
                );
                if (!currentCustomDays) {
                  setValue(
                    DocumentFormFieldNames.RenewalReminderCustomDays,
                    1,
                    { shouldValidate: true },
                  );
                }
              }
            }
          },
        },
        {
          name: DocumentFormFieldNames.RenewalReminderCustomDays,
          element: Input,
          getLeftLabel: () => t('every'),
          getRightLabel: () => t('days'),
          componentProps: {
            ...DEFAULT_NUMBER_INPUT_PROPS,
            hasErrorAsTooltip: true,
          },
          isVisibleFn: (values: Values) =>
            !!_get(values, DocumentFormFieldNames.RenewalReminderEnabled) &&
            _get(values, [
              DocumentFormFieldNames.RenewalReminderRepetition,
              'value',
            ]) === DocumentationRenewalRepetitionEnum.Custom,
          validation: Yup.string().when(
            [
              DocumentFormFieldNames.RenewalReminderEnabled,
              DocumentFormFieldNames.RenewalReminderRepetition,
            ],
            (deps, schema) => {
              const isEnabled = _get(deps, 0);
              const repetitionValue = _get(deps, [1, 'value']);
              return isEnabled &&
                repetitionValue === DocumentationRenewalRepetitionEnum.Custom
                ? schema
                    .test(
                      'is-number',
                      t('number-field-error'),
                      (value) => !_isNaN(value),
                    )
                    .test('is-min', t('number-field-error-min-1'), (value) => {
                      const number = value ? parseFloat(value) : 0;
                      return number >= 1;
                    })
                    .test(
                      'is-max',
                      t('number-field-error-max', {
                        max: RENEWAL_CUSTOM_DAYS_MAX,
                      }),
                      (value) => {
                        const number = value ? parseFloat(value) : 0;
                        return number <= RENEWAL_CUSTOM_DAYS_MAX;
                      },
                    )
                    .required(t('required-field-error'))
                : schema.nullable();
            },
          ),
          formItemClassName: formStyles.formColumnAuto,
          formItemElementWrapClassName: styles.inputNumber,
        },
        {
          name: DocumentFormFieldNames.RenewalReminderEarlyReminderTitle,
          isVisibleFn: (values: Values) =>
            !!_get(values, DocumentFormFieldNames.RenewalReminderEnabled),
          customRender: () => (
            <Typography variant="body" strong key="earlyReminder">
              {t('early-reminder')}
            </Typography>
          ),
        },
        {
          name: DocumentFormFieldNames.RenewalReminderEarlyAmount,
          element: Input,
          formItemClassName: formStyles.formColumnAuto,
          formItemElementWrapClassName: styles.inputNumber,
          isVisibleFn: (values: Values) =>
            !!_get(values, DocumentFormFieldNames.RenewalReminderEnabled),
          componentProps: {
            ...DEFAULT_NUMBER_INPUT_PROPS,
            hasErrorAsTooltip: true,
          },
          validation: Yup.string().when(
            DocumentFormFieldNames.RenewalReminderEnabled,
            (deps, schema) =>
              _get(deps, 0)
                ? schema
                    .test(
                      'is-number',
                      t('number-field-error'),
                      (value) => !value || !_isNaN(value),
                    )
                    .test('is-min', t('number-field-error-min-1'), (value) => {
                      if (!value) return true;
                      const number = parseFloat(value);
                      return number >= 1;
                    })
                    .test(
                      'is-max',
                      t('number-field-error-max', {
                        max: RENEWAL_EARLY_REMINDER_DAYS_MAX,
                      }),
                      (value) => {
                        if (!value) return true;
                        const number = parseFloat(value);
                        return number <= RENEWAL_EARLY_REMINDER_DAYS_MAX;
                      },
                    )
                    .nullable()
                : schema.nullable(),
          ),
          getRightLabel: () => t('days-before-deadline'),
          hideErrorsSpace: true,
          hideErrors: true,
        },
      ],
    },
  ];
}
